import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ky from "ky";

import { PRODUCT } from "../seo/siteMeta";

/**
 * Grouped rather than one flat row of links.
 *
 * The footer is the only place every page is reachable from (the mobile drawer is unmounted when
 * closed, so these are the internal links a crawler actually finds in the prerendered HTML), and it
 * is where visitors look for the trust and legal pages. Grouping keeps that discoverable as the page
 * count grows instead of turning into an undifferentiated line of nine links.
 */
const footerSections = [
  {
    title: "Product",
    links: [
      { text: "Pricing", link: "/pricing" },
      { text: "Live demo", link: "/demo" },
      { text: "FAQs", link: "/faqs" },
    ],
  },
  {
    title: "Company",
    links: [
      { text: "About", link: "/about" },
      { text: "Contact", link: "/contact" },
    ],
  },
  {
    title: "Trust & legal",
    links: [
      { text: "Security", link: "/security" },
      { text: "Privacy policy", link: "/privacy" },
      { text: "Terms of service", link: "/terms" },
      { text: "Accessibility", link: "/accessibility" },
    ],
  },
];

const Footer = () => {
  const theme = useTheme(); // Use the theme for styling
  const [apiVersion, setApiVersion] = useState("");
  // Resolved after mount so the prerenderer does not bake a build-time version
  // string into the static HTML; start-nginx.sh rewrites config.js at boot.
  const [appVersion, setAppVersion] = useState("");
  const footerRef = useRef(null);

  useEffect(() => {
    setAppVersion(window._env_?.REACT_APP_VERSION || "");
  }, []);

  useEffect(() => {
    if (!footerRef?.current?.clientHeight) {
      return;
    }
  }, [footerRef?.current?.clientHeight]);

  useEffect(() => {
    ky.get(window._env_?.REACT_APP_API_URL + "/actuator/info", {
      timeout: window._env_?.REACT_APP_TIMEOUT,
    })
      .json()
      .then((response) => {
        setApiVersion(response.app.version);
      })
      .catch((e) => {
        setApiVersion("unknown");
        console.error(e);
      });
  }, []);

  return (
    <Box
      component="footer"
      ref={footerRef}
      sx={{
        width: "100%",
        height: "auto",
        backgroundColor: theme.palette.footer.backgroundColor, // Use custom color
        paddingTop: "2rem",
        paddingBottom: "1.5rem",
        textAlign: "center",
        // Was primary.contrastText, which is a near-white green tint: invisible against the white
        // light-mode footer for any text that did not set its own colour.
        color: theme.palette.text.primary,
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          component="nav"
          aria-label="Footer"
          spacing={3}
          sx={{ textAlign: "left", mb: 3 }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Link
              component={RouterLink}
              to="/"
              color="text.primary"
              underline="hover"
              variant="subtitle2"
              sx={{ fontWeight: "bold" }}
            >
              {PRODUCT.name}
            </Link>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, maxWidth: 280 }}
            >
              {PRODUCT.tagline}.
            </Typography>
          </Grid>
          {footerSections.map((section) => (
            <Grid item xs={12} sm={6} md={3} key={section.title}>
              {/* A real heading rather than styled text. These labels are what make the footer
                  navigable by heading, which is how a screen reader user skims a block of links
                  instead of hearing all eleven of them in a row to find the legal ones. */}
              <Typography
                component="h2"
                variant="subtitle2"
                color="text.primary"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {section.links.map((item) => (
                  <Link
                    key={item.text}
                    component={RouterLink}
                    to={item.link}
                    color="text.secondary"
                    variant="body2"
                    underline="hover"
                  >
                    {item.text}
                  </Link>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>
        <Grid container direction="column" alignItems="center">
          <Grid item xs={12}>
            <Tooltip title="Jessie helped too :p" arrow>
              <Typography
                color="text.primary"
                variant="subtitle1"
                align="center"
              >
                Copyright{" "}
                <Link
                  href="https://byerline.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                >
                  Jacob Byerline
                </Link>{" "}
                © {new Date().getFullYear()}
              </Typography>
            </Tooltip>
            {/* Stated wherever the product is described, because a unit's leadership will
                reasonably want to know whether this is a council system. It is not. */}
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ display: "block", mt: 1, maxWidth: 620, mx: "auto" }}
            >
              BadgeReg is independent software and is not run by, endorsed by,
              or affiliated with a Scouting council or the Boy Scouts of
              America.
            </Typography>
            {appVersion && (
              <Typography
                color="text.secondary"
                variant="caption"
                align="center"
                sx={{ display: "grid", mt: 1 }}
              >
                UI: v{appVersion}{" "}
                {!["", "unknown"].includes(apiVersion) && "API: v" + apiVersion}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer;
